/**
 * Seller Service
 * 
 * Responsible for fetching collection data for the seller utility.
 * Handles fetching with version info needed for VFM links.
 */

import { fetchResponse } from './fetch.js';
import { parseXml } from '../helpers';
import { CollectionItem } from '../objects';

/**
 * Fetch a user's collection with version information
 * 
 * @param {string} username - BGG username
 * @param {Object} options - Filter options
 * @param {Object} options.collectionParams - Additional BGG collection parameters
 * @returns {Promise<{items: CollectionItem[], error: string|null}>}
 */
export const fetchCollectionForSale = async (username, options = {}) => {
    const { collectionParams = {} } = options;
    
    let queryParams = `username=${username}`;
    
    // Apply collection parameters
    Object.entries(collectionParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            // Convert boolean values to 1/0 for BGG API
            const paramValue = typeof value === 'boolean' ? (value ? '1' : '0') : value;
            if (paramValue === '1')
                queryParams += `&${encodeURIComponent(key)}=${encodeURIComponent(paramValue)}`;
        }
    });
    
    let iterations = 1;
    const maxIterations = 5;
    let result = null;
    
    // Retry logic for BGG API (it sometimes returns 202 initially)
    const collectionPath = encodeURIComponent(`/collection?${queryParams}`);
    while (true) {
        result = await fetchResponse(`/.netlify/functions/fetch-bgg-xml?path=${collectionPath}`);
        
        if (!result) {
            return { items: [], error: 'Failed to fetch collection. Please try again.' };
        }
        
        // BGG returns 202 when the collection is being prepared
        if (result.status === 202 || result.status === '202') {
            if (iterations >= maxIterations) {
                return { items: [], error: 'Collection is taking too long to load. Please try again in a moment.' };
            }
            
            // Wait 3 seconds and try again
            await new Promise(resolve => setTimeout(resolve, 3000));
            iterations++;
            continue;
        }
        
        if (result.status !== 200 && result.status !== '200') {
            if (result.status === 404 || result.status === '404') {
                return { items: [], error: 'User not found on Board Game Geek' };
            }
            return { items: [], error: `Error fetching collection: ${result.status}` };
        }
        
        break;
    }
    
    try {
        const xmlDoc = await parseXml(result.data);
        
        // Check for errors in the response
        const errorElement = xmlDoc.getElementsByTagName('error')[0];
        if (errorElement) {
            const errorContent = errorElement.textContent || errorElement.getAttribute('message') || 'Unknown error';
            if (errorContent.includes('private')) {
                return { items: [], error: 'This user has set their collection to private' };
            }
            return { items: [], error: errorContent };
        }
        
        const items = xmlDoc.querySelectorAll("item[objecttype=thing]");
        const collectionItems = [];
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const collectionItem = CollectionItem.fromXmlWithVersion(item);
            collectionItems.push(collectionItem);
        }
        
        // Sort alphabetically by name
        collectionItems.sort((a, b) => a.name.localeCompare(b.name));
        
        return { items: collectionItems, error: null };
    } catch (error) {
        console.error('Error parsing collection XML:', error);
        return { items: [], error: 'Error parsing collection data' };
    }
};

/**
 * Generate a BGG Virtual Flea Market add link
 * 
 * @param {Object} params - Parameters for the VFM link
 * @param {string} params.geeklistId - The VFM geeklist ID
 * @param {string} params.gameId - BGG game object ID
 * @param {string} params.imageId - Image ID for the listing
 * @param {string} params.customBody - Complete BBCode body text (includes price, version, condition, notes, etc.)
 * @returns {string} The full VFM add URL
 */
export const generateVfmLink = (params) => {
    const { geeklistId, gameId, imageId, customBody = '' } = params;
    
    const baseUrl = `https://boardgamegeek.com/geeklist/${geeklistId}`;
    
    // URL encode the body
    const encodedBody = encodeURIComponent(customBody);
    
    // Build the full URL
    let url = `${baseUrl}?addListitem=1&addListitemType=things&addListitemId=${gameId}`;
    if (imageId) {
        url += `&addListitemImageid=${imageId}`;
    }
    url += `&addListitemBody=${encodedBody}`;
    
    return url;
};

/**
 * Calculate recommended price based on percentage of average market price
 * 
 * @param {number} averagePrice - Average market price
 * @param {number} percentage - Percentage to apply (e.g., 80 for 80%)
 * @returns {number} Recommended price rounded to 2 decimal places
 */
export const calculateRecommendedPrice = (averagePrice, percentage) => {
    if (!averagePrice || averagePrice <= 0) {
        return 0;
    }
    const recommended = (averagePrice * percentage) / 100;
    return Math.round(recommended * 100) / 100;
};

const sellerService = {
    fetchCollectionForSale,
    generateVfmLink,
    calculateRecommendedPrice
};

export default sellerService;